import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import formatPrice from "@/core/helpers/formatPrice";
import useAppSelector from "@/core/helpers/useAppSelector";
import { BankCardIcon, CashIcon, RubleIcon } from "@/shared/icons";
import { Button, Modal } from "@/shared/ui";
import Loader from "@/shared/ui/Loader";
import OfferCheckbox from "@/shared/widgets/OfferCheckbox";
import OfferLocation from "@/shared/widgets/OfferLocation";
import OfferPlayerInfo from "@/shared/widgets/OfferPlayerInfo";
import OfferView from "@/shared/widgets/OfferView";
import { offerResolverSelector } from "@/domains/offer/slice";
import getBusinessImage from "@/domains/business/helpers/getBusinessImage";
import useOfferResolver from "@/domains/offer/hooks/useOfferResolver";
import useOfferResolverBusiness from "@/domains/offer/hooks/useOfferResolverBusiness";

export default function OfferEstateBusinessResolverContainer({ isShow }: { isShow: boolean }) {
  const { t } = useTranslation("offer");

  const offerResolver = useAppSelector(offerResolverSelector);

  const [isAgree, setIsAgree] = useState(false);

  const { business, pending } = useOfferResolverBusiness(isShow);

  const { pending: isLoading, resolveByCard, resolveByCash, reject } = useOfferResolver();

  const level = business ? t(`business:business.level.${business.level}`) : "";

  const src = business ? getBusinessImage(business.id) : "";
  const srcLogo = business ? getBusinessImage(business.id, "logo") : "";

  const address = business
    ? `${t("offer.saintPetersburg")}, ${business?.address?.streetName} ${business?.address?.index}`
    : "";

  const toggleAgree = useCallback(() => {
    setIsAgree((state) => !state);
  }, []);

  const isDisabled = useMemo(() => !isAgree, [isAgree]);

  useEffect(() => {
    if (!isShow) {
      setIsAgree(false);
    }
  }, [isShow]);

  const notEmpty = !pending && !!business;

  const footer = (
    <div className={clsx("grid grid-cols-2 gap-2", !notEmpty && "!opacity-0 pointer-events-none")}>
      <Button
        className="h-11"
        onClick={resolveByCash}
        disabled={isDisabled || pending || isLoading}
        loading={pending || isLoading}
      >
        <CashIcon className="w-5 mr-2.5" />
        {t("offer.buy")}
      </Button>
      <Button
        className="h-11"
        onClick={resolveByCard}
        disabled={isDisabled || pending || isLoading}
        loading={pending || isLoading}
      >
        <BankCardIcon className="w-4.5 mr-2.5" />
        {t("offer.buy")}
      </Button>
    </div>
  );

  const leftText = (
    <div className="flex items-center gap-2.5">
      <RubleIcon size={18} />
      <div className="flex flex-col gap-0.5">
        <p className="text-14 font-bold">{formatPrice(business?.price)} ₽</p>
        <p className="text-11 font-medium uppercase">{t("offer.estate.info.price")}</p>
      </div>
    </div>
  );

  const rightText = (
    <div className="flex flex-col gap-0.5 items-end ml-auto">
      <p className="text-14 font-bold">#{business?.id}</p>
      <p className="text-11 font-medium uppercase">{t("offer.business.creator.info.businessId")}</p>
    </div>
  );

  return (
    <Modal
      open={isShow}
      title={t("offer.business.trade.resolver.title")}
      onClose={reject}
      footer={footer}
      className="w-[30rem] pt-6.5 pb-7.5 pl-7.5 pr-6.5"
      classNameCloseBtn="top-6.5 right-7.5"
      dataMzAccent="lightBlue"
      style={{ fontFamily: "TT Norms Pro", boxShadow: "0px 0px 200px 0px rgba(0, 121, 194, 0.30)" }}
    >
      <div className="relative flex flex-col gap-4 w-full min-h-[36rem]">
        {!notEmpty || pending ? (
          <div className="absolute top-0 left-0 z-3 flex justify-center items-center size-full text-16 font-bold text-white/50 text-center leading-none uppercase">
            {!pending && t("offer.empty")}
            {pending && <Loader />}
          </div>
        ) : (
          <>
            <OfferPlayerInfo
              name={offerResolver?.baseCharacter?.name}
              avatar={offerResolver?.baseCharacter?.avatar}
              id={offerResolver?.baseCharacter?.id}
              text={t("offer.seller")}
            />
            <div>
              <div className="mb-3 text-inverted text-16 font-medium">
                {t("offer.business.trade.resolver.business")}:
              </div>
              <OfferView
                src={src}
                level={level}
                leftText={leftText}
                rightText={rightText}
                isLoading={pending}
              />
            </div>
            <OfferLocation address={address} />
            <div className="flex justify-between items-center pt-5 pb-1 border-t border-[#191919]">
              <div className="flex flex-col gap-2">
                <p className="text-16 font-bold leading-none">{business?.name}</p>
                <p className="text-15 font-medium text-white/50 leading-none">
                  {t("offer.business.trade.resolver.info.name")}
                </p>
              </div>
              <div
                className="relative flex w-[6.6875rem] h-6"
                style={{
                  backgroundImage: `url(${srcLogo})`,
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "contain",
                }}
              />
            </div>
            <div>
              <div className="text-18 font-bold text-accent-solid">
                {formatPrice(offerResolver?.price)} ₽
              </div>
              <div className="text-15 font-medium text-gray/50">{t("offer.suggestedCost")}</div>
            </div>
            <OfferCheckbox
              label={t("offer.business.trade.resolver.agree")}
              isChecked={isAgree}
              onChange={toggleAgree}
            />
          </>
        )}
      </div>
    </Modal>
  );
}
